import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

import { AccordionButton } from '@/components/Button/AccordionButton';
import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import { Grid, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { defaultNS, useTranslation } from '@/lib/i18n';

export const Route = createFileRoute('/_staff/places/add-select/')({
  component: PlaceTypeSelection,
});

type CommonProps = {
  selectedPlaceType: string | null;
  setSelectedPlaceType: Dispatch<SetStateAction<string | null>>;
};

function PlaceTypeSelection() {
  const [selectedPlaceType, setSelectedPlaceType] = useState<string | null>(
    'place',
  );

  return (
    <VStack className="mx-auto w-[1000px]">
      <Header />
      <Spacer variant="xs" />
      <PlaceTypeRadioGroup
        selectedPlaceType={selectedPlaceType}
        setSelectedPlaceType={setSelectedPlaceType}
      />
      <Spacer variant="xxl" />
      <AdditionalOptions />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.places.addSelect.header',
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <Text variant="h2">{t('headerTitle')}</Text>
    </VStack>
  );
}

function PlaceTypeRadioGroup({
  selectedPlaceType,
  setSelectedPlaceType,
}: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.places.addSelect.body',
  });

  const PLACE_TYPES: {
    id: string;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
  }[] = [
    {
      id: 'place',
      title: t('place'),
      requiredDetails: [
        { iconName: 'SquareDot', text: t('itemCard.placeName') },
        { iconName: 'SquareDot', text: t('itemCard.postalCode') },
        { iconName: 'SquareDot', text: t('itemCard.prefecture') },
        { iconName: 'SquareDot', text: t('itemCard.city') },
        { iconName: 'SquareDot', text: t('itemCard.address') },
      ],
    },
  ];

  const navigate = useNavigate();

  const handleRadioChange = useCallback(
    (value: string) => {
      setSelectedPlaceType(value);
    },
    [setSelectedPlaceType],
  );

  const handleButtonClick = useCallback(() => {
    if (selectedPlaceType) {
      navigate({
        to: '/places/add',
        hash: 'basic',
      });
    }
  }, [selectedPlaceType, navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4">{t('selectPlaceType')}</Text>
      <Spacer variant="l" />
      <RadioGroup
        onValueChange={handleRadioChange}
        value={selectedPlaceType ?? 'place'}
      >
        <Grid columns={2} rowGap={28} columnGap={24}>
          {PLACE_TYPES.map((type) => (
            <AttributeCard
              key={type.id}
              title={type.title}
              titleStyle="CardHeader"
              onClick={() => handleRadioChange(type.id)}
              actionButton={
                <RadioGroupItem
                  id={type.id}
                  value={type.id}
                  className="h-4 w-4"
                  checked={selectedPlaceType === type.id}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className="cursor-pointer rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md"
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t('itemCard.requiredDetails')}
                </Text>
                {type.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t('fillOutDetails')}
      </ActionButton>
    </>
  );
}

function AdditionalOptions() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.places.addSelect.options',
  });
  const navigate = useNavigate();

  const handleCsvImportClick = useCallback(() => {
    navigate({ to: '/places/import-csv', hash: 'upload-file' });
  }, [navigate]);

  return (
    <VStack className="w-full">
      <Text variant="labelSemiLarge">{t('description')}</Text>
      <Spacer size={24} />
      <VStack className="w-full">
        {/* CSV一括アップロード */}
        <AccordionButton
          icon={
            <Icon
              iconName="DatabaseBackup"
              text={t('bulkCsvUpload.title')}
              textColor="text-primary"
              colorVariant="primary"
            />
          }
          className="w-full"
        >
          <VStack className="gap-2">
            <div className="flex flex-col items-start gap-2 text-neutral-on-fill">
              <Text variant="p">{t('bulkCsvUpload.description')}</Text>
            </div>
            <div>
              <ActionButton
                size="sm"
                variant="default"
                icon="ArrowRightIcon"
                iconPosition="right"
                onClick={handleCsvImportClick}
              >
                {t('bulkCsvUpload.button')}
              </ActionButton>
            </div>
          </VStack>
        </AccordionButton>

        <Spacer size={24} />

        {/* 外部システム同期 - 開発中 */}
        <AccordionButton
          icon={
            <Icon
              iconName="DatabaseBackup"
              text={t('bulkRegister')}
              textColor="text-primary"
              colorVariant="primary"
            />
          }
          inDevelopment
          className="w-full"
        />
      </VStack>
    </VStack>
  );
}
